import { useCallback } from 'react';
import { clientMasterApi } from '@/services/api';

/**
 * Returns a unified client API using ClientMaster as the single source.
 * Legacy clientsApi (/api/clients/) is no longer available.
 *
 * All returned functions are stable references (useCallback) so they are
 * safe to use as useCallback / useMemo dependencies without causing
 * extra re-renders or runaway effect loops.
 */
export const useClientApi = () => {
    // Dropdown use: always restrict to PMS clients so AIF-only accounts don't appear in selects.
    const getClients = useCallback(
        (params: Parameters<typeof clientMasterApi.getClients>[0] = {}) =>
            clientMasterApi.getClients({ ...params, client_type: 'PMS' as const }),
        [],
    );

    const getClient = useCallback(
        (id: number) => clientMasterApi.getClient(id),
        [],
    );

    /**
     * Returns the client_master filter object for client-based API queries.
     * Use spread operator: { ...getClientFilter(id) }
     */
    const getClientFilter = useCallback(
        (clientId?: number | string) => {
            const id = typeof clientId === 'string' ? parseInt(clientId, 10) : clientId;
            if (id == null || isNaN(id)) return {};
            return { client_master: id };
        },
        [],
    );

    return { getClients, getClient, isClientMaster: true as const, getClientFilter };
};
